import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { Icon, IconButton, Typography } from '@/ui';

import { longDate } from '../format';

import PromptCard from './PromptCard';
import { reflectionV2Sections } from './reflectionV2Items';

import type { ReflectionV2Item } from './reflectionV2Items';
import type { ReflectionView } from '@/store/api/mcpMemberApi';
import type { FC } from 'react';

export interface ReflectionV2ViewProps {
  aiName: string | null;
  highlighted: ReadonlySet<string>;
  onBack: () => void;
  onCopyPrompt: (text: string) => void;
  onCopyReflection: () => void;
  onFeedback: (value: 'positive' | 'negative') => void;
  onToggleHighlight: (item: ReflectionV2Item, on: boolean) => void;
  // False while the member's grants are unknown, so a connected member is
  // never asked to connect.
  showConnectPrompt?: boolean;
  transcriptHref: string | null;
  view: ReflectionView;
}

const promptsBox = 'mt-10 rounded-3xl bg-sage-200 p-6';

const ReflectionV2View: FC<ReflectionV2ViewProps> = ({
  aiName,
  highlighted,
  onBack,
  onCopyPrompt,
  onCopyReflection,
  onFeedback,
  onToggleHighlight,
  showConnectPrompt = true,
  transcriptHref,
  view,
}) => {
  const { reflection } = view;
  const prompts = reflection.ai_prompts;
  const reason = reflection.no_prompts_reason ?? '';

  return (
    <div>
      <header className="flex items-center justify-between px-4 py-3">
        <IconButton aria-label="Back" onClick={onBack}>
          <Icon>arrow_back</Icon>
        </IconButton>
        <div className="flex gap-1">
          <IconButton
            aria-label="Copy reflection"
            title="Copy reflection"
            onClick={onCopyReflection}
          >
            <Icon>content_copy</Icon>
          </IconButton>
          <IconButton
            aria-label="Helpful"
            title="Helpful"
            onClick={() => onFeedback('positive')}
          >
            <Icon>thumb_up_off_alt</Icon>
          </IconButton>
          <IconButton
            aria-label="Not helpful"
            title="Not helpful"
            onClick={() => onFeedback('negative')}
          >
            <Icon>thumb_down_off_alt</Icon>
          </IconButton>
        </div>
      </header>
      <main className="mx-auto max-w-[600px] px-6 pb-36 pt-6">
        <p className="m-0 text-cement-400">
          For your <b>{longDate(view.session)} Session</b>
        </p>
        <Typography className="mb-6 mt-2" component="h1" variant="h4">
          Session Reflection
        </Typography>
        {reflectionV2Sections(reflection).map((section) => (
          <section
            key={section.key}
            aria-labelledby={`v2-${section.key}`}
            className="mb-7"
          >
            <Typography
              className="mb-2"
              component="h2"
              id={`v2-${section.key}`}
              variant="h6"
            >
              {section.title}
            </Typography>
            {section.items.map((item) => {
              const on = highlighted.has(item.key);
              return (
                <div
                  key={item.key}
                  className={clsx(
                    'relative -mx-3 mb-2.5 rounded-[10px] py-1.5 pr-11 text-[15.5px] leading-relaxed',
                    section.bullet
                      ? 'pl-[30px] before:absolute before:left-[15px] before:top-4 before:size-1 before:rounded-full before:bg-cement-500 before:content-[""]'
                      : 'pl-3',
                    { 'bg-sunny-300': on },
                  )}
                  data-highlighted={on ? 'true' : undefined}
                >
                  {item.headline && (
                    <b className="block font-semibold">{item.headline}</b>
                  )}
                  {item.text}
                  <IconButton
                    aria-label={on ? 'Remove highlight' : 'Highlight'}
                    aria-pressed={on}
                    className="absolute right-1 top-1"
                    size="small"
                    title={on ? 'Remove highlight' : 'Highlight'}
                    onClick={() => onToggleHighlight(item, !on)}
                  >
                    <Icon>push_pin</Icon>
                  </IconButton>
                </div>
              );
            })}
          </section>
        ))}
        {prompts.length > 0 && (
          <div className={promptsBox}>
            <Typography component="h2" variant="h5">
              Take this to your AI
            </Typography>
            <Typography className="mb-4 text-cement-400" variant="body1">
              {aiName
                ? `Copy one into ${aiName} when the moment comes. It will pull in your coaching notes.`
                : 'Copy one into the AI you use when the moment comes.'}
            </Typography>
            {prompts.map((prompt) => (
              <PromptCard
                key={prompt.text}
                prompt={prompt}
                onCopy={onCopyPrompt}
              />
            ))}
            {!aiName && showConnectPrompt && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-cement-400">
                <span>
                  Connect your AI and these prompts will use your coaching
                  notes.
                </span>
                <Link className="text-sage-500" to="/m/settings/connect-ai">
                  Connect your AI
                </Link>
              </div>
            )}
          </div>
        )}
        {prompts.length === 0 && reason !== '' && (
          <div className={promptsBox}>
            <Typography component="h2" variant="h5">
              Take this to your AI
            </Typography>
            <Typography className="mb-4 text-cement-400" variant="body1">
              Ideas for using your AI with what came up in this session.
            </Typography>
            <div className="rounded-2xl bg-white p-4 text-cement-400">
              <span>
                No prompts for this one. {reason}{' '}
                <Link className="text-sage-500" to="/m/settings/ai-moments">
                  Change this
                </Link>
              </span>
            </div>
          </div>
        )}
        {transcriptHref && (
          <div className="mt-8">
            <Link className="text-sage-500" to={transcriptHref}>
              View transcript
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReflectionV2View;
